/**
 * The app.
 *
 * Three states and no router yet: signed out, on the path, or in a lesson. A
 * lesson takes over the whole screen — no tab bar, one way out — because that is
 * what the mockups show and because a child mid-question should not have four
 * other places to tap.
 *
 * Every class name here — `.frame`, `.tabs`, `.card`, `.btn`, `.bubble` — comes
 * from the mockup stylesheet, unchanged
 * ([D-061](../../../docs/DECISIONS.md#d-061)). `cssguard.py` polices ownership
 * of those names, so the app inherits that check for free.
 */

import { TABS, type TabKey } from '@capytalk/tokens';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import Boundary from './Boundary.js';
import GrownUps from './GrownUps.js';
import { HintProvider } from './Hint.js';
import Lesson from './Lesson.js';
import Path from './Path.js';
import Quests from './Quests.js';
import Shop from './Shop.js';
import Badges from './Badges.js';
import Bonus from './Bonus.js';
import { readDevice } from './device.js';
import { keepSignedIn, whatNext } from './renewal.js';
import { applyPrefs, loadPrefs } from './prefs.js';
import { setLearning } from './language.js';
import Settings from './Settings.js';
import { unlockVoice } from './audio.js';
import { effectsOn, setEffects, warmUp } from './effects.js';
import Pets from './Pets.js';
import Profile from './Profile.js';
import Wardrobe from './Wardrobe.js';
import Library from './Library.js';
import { reportTo } from './telemetry.js';
import Wallet from './Wallet.js';
import SignIn from './SignIn.js';
import {
  type Client,
  type Session,
  createClient,
  readSession,
  writeSession,
} from './api.js';

/**
 * Stand-ins for the drawn icon set.
 *
 * The real icons are commissioned art ([TODO](../../../TODO.md)); these keep the
 * bar the right shape and the right size meanwhile, and are the one place to
 * swap when the artwork lands.
 */
/**
 * What `playing` holds for a practice session.
 *
 * A sentinel rather than a second piece of state: "which lesson, or the pool, or
 * neither" is one question, and splitting it into two booleans is how a screen
 * ends up in both at once.
 */
const PRACTICE = 'practice';

const ICONS: Readonly<Record<TabKey, string>> = {
  learn: '🌳',
  quests: '🎯',
  shop: '🛍️',
  letters: '🔤',
  profile: '🦫',
};

function Tabs({
  active,
  onSelect,
}: {
  readonly active: TabKey;
  readonly onSelect: (key: TabKey) => void;
}) {
  return (
    <nav className="tabs" aria-label="Main menu">
      {/*
        Only on a wide screen, where the bar becomes a sidebar (D-164, mockup
        20). Hidden from screen readers: the page already says what app it is.
      */}
      <span className="side-brand" aria-hidden="true">
        CapyTalk
      </span>
      {TABS.map((tab) => (
        <a
          key={tab.key}
          href={`#${tab.key}`}
          className={tab.key === active ? 'on' : undefined}
          aria-current={tab.key === active ? 'page' : undefined}
          // The bar is icons only, because a child who cannot yet read English
          // has to be able to navigate it. The label is what a screen reader
          // announces (ACCESSIBILITY.md).
          aria-label={tab.label}
          onClick={() => {
            onSelect(tab.key);
          }}
        >
          <span aria-hidden="true">{ICONS[tab.key]}</span>
          {/*
            The words beside the icon, on a wide screen only; on a phone the bar
            stays icons. `aria-hidden`, because the link's label already says it.
          */}
          <span className="tab-word" aria-hidden="true">
            {tab.label}
          </span>
        </a>
      ))}
    </nav>
  );
}

/** The way back, when the grown-up screen itself will not render. */
function GrownUpsBroken({ onLeave }: { readonly onLeave: () => void }) {
  return (
    <div className="frame">
      <div className="grow pad center">
        <p className="sub" role="alert">
          That screen broke. Please try again.
        </p>
        <button className="btn" type="button" onClick={onLeave}>
          Back
        </button>
      </div>
    </div>
  );
}

export default function App({
  /**
   * The API client, for tests that want to answer without a server.
   *
   * The app builds its own; this only exists so a component test can drive the
   * screens against a stub rather than against `fetch`.
   */
  client: injected,
}: {
  readonly client?: Client;
} = {}) {
  const [session, setSession] = useState<Session | null>(() => readSession());
  const [tab, setTab] = useState<TabKey>('learn');
  /** The settings screen, which sits over the tabs rather than being one. */
  const [settings, setSettings] = useState(false);
  /** Which half of the profile tab is showing. */
  const [mine, setMine] = useState<'me' | 'things' | 'pets' | 'badges'>('me');
  /** The lesson being played, or null when the path is showing. */
  /**
   * What is being played.
   *
   * `null` is the path. A string is a lesson; `'practice'` is the mistakes pool,
   * which is a session with no lesson at all
   * ([D-074](../../../docs/DECISIONS.md#d-074)).
   */
  const [playing, setPlaying] = useState<string | null>(null);
  /**
   * The pass the child chose on the path, carried into the runner
   * ([D-109](../../../docs/DECISIONS.md#d-109)).
   *
   * Beside `playing` rather than inside it, so the practice sentinel keeps the
   * shape it has always had — and a replay level is not a thing practice can
   * have anyway.
   */
  const [replayLevel, setReplayLevel] = useState<number | undefined>(undefined);
  /**
   * The day a bonus round was taken up on, or null
   * ([D-024](../../../docs/DECISIONS.md#d-024)).
   *
   * The day rather than a boolean, because the server keys the whole round on
   * it — and the day is the child's own, from the device that finished the
   * lesson, so carrying it is more honest than asking again at midnight.
   */
  const [bonusDay, setBonusDay] = useState<string | null>(null);
  /**
   * Whether the effects channel is on.
   *
   * Mirrored into state so the button redraws; `effects.ts` is the truth and
   * survives a reload. Read once on mount rather than on every render, because
   * it touches `localStorage`.
   */
  const [sound, setSound] = useState(effectsOn);
  /** The grown-up screen, which sits in front of the app rather than inside it. */
  const [grownUps, setGrownUps] = useState(false);

  // Read through a ref so the client is built once and still sees the current
  // token: rebuilding it on every sign-in would throw away in-flight requests.
  const current = useRef<Session | null>(session);
  current.current = session;
  const built = useMemo(() => createClient(() => current.current?.token ?? null), []);
  const client = injected ?? built;

  // The one place an error boundary can reach, because a boundary sits above the
  // tree and knows nothing about the app inside it
  // ([Telemetry](../../../docs/TELEMETRY.md)).
  useEffect(() => {
    reportTo(client);
    return () => {
      reportTo(null);
    };
  }, [client]);

  /*
    Load the sound set on the first tap anywhere
    ([Sound](../../../docs/SOUND.md)).

    Not on mount: browsers refuse to construct audio before a gesture, and a
    console full of autoplay warnings at launch is how a real failure gets
    missed later. `once` means this costs one listener for one tap and then
    nothing — and the set has to be *preloaded*, because the feedback cue must
    land within 50 ms of a tap or it feels detached from it.
  */
  useEffect(() => {
    const load = () => {
      warmUp();
    };
    globalThis.addEventListener?.('pointerdown', load, { once: true });
    globalThis.addEventListener?.('keydown', load, { once: true });
    return () => {
      globalThis.removeEventListener?.('pointerdown', load);
      globalThis.removeEventListener?.('keydown', load);
    };
  }, []);

  /*
    Let Sneakers' recordings play on an iPad (D-195).

    On the first *completed* tap, not the first touch: Safari counts a touch
    as a gesture only when it ends, so `pointerdown` from a finger would not
    do. `click` covers the finger and the mouse, `keydown` the keyboard. After
    this, a line that starts when a screen has loaded plays as Alice instead
    of falling through to the device's voice.
  */
  useEffect(() => {
    let done = false;
    const unlock = () => {
      if (done) return;
      done = true;
      unlockVoice();
      globalThis.removeEventListener?.('click', unlock, true);
      globalThis.removeEventListener?.('touchend', unlock, true);
      globalThis.removeEventListener?.('keydown', unlock, true);
    };
    globalThis.addEventListener?.('click', unlock, true);
    globalThis.addEventListener?.('touchend', unlock, true);
    globalThis.addEventListener?.('keydown', unlock, true);
    return () => {
      globalThis.removeEventListener?.('click', unlock, true);
      globalThis.removeEventListener?.('touchend', unlock, true);
      globalThis.removeEventListener?.('keydown', unlock, true);
    };
  }, []);

  const signIn = useCallback((next: Session) => {
    writeSession(next);
    setSession(next);
  }, []);

  const signOut = useCallback(() => {
    writeSession(null);
    setSession(null);
    setPlaying(null);
  }, []);

  /*
    Keep the child signed in for the evening (D-131). A token lives fifteen
    minutes; this renews it with six to spare, and signs in again from the
    remembered device when a tablet slept through the expiry. Checked every
    minute and the moment the tab is looked at again, because waking a
    sleeping iPad is exactly when the token is dead.
  */
  /*
    A child's display settings belong to them, not to the tablet (D-132,
    D-133), so they follow whoever is signed in — and come off at sign-out, or
    the sibling who taps their face next would inherit them.
  */
  const learnerId = session?.learnerId ?? null;
  useEffect(() => {
    applyPrefs(learnerId === null ? null : loadPrefs(learnerId));
  }, [learnerId]);

  const signedIn = session !== null;

  const checking = useRef(false);
  /*
    Whether the session is known good. False only when the app opens holding a
    token that is expiring or already dead — a tablet woken from sleep — and
    only until the first check settles. Without it the path asked for the map
    with the dead token, a moment before the renewal landed, and showed "The
    map would not load" to a child who was, in fact, signed in (D-131).
  */
  const [ready, setReady] = useState(
    () => session === null || whatNext(session.token, Date.now()) === 'wait',
  );
  useEffect(() => {
    if (!signedIn) return;
    const check = () => {
      const now = current.current;
      if (checking.current || now === null) return;
      checking.current = true;
      void keepSignedIn(client, now, readDevice(), Date.now())
        .then((next) => {
          if (next === 'unchanged') return;
          if (next === null) signOut();
          else signIn(next);
        })
        .finally(() => {
          checking.current = false;
          setReady(true);
        });
    };
    check();
    const timer = setInterval(check, 60_000);
    const onVisible = () => {
      if (globalThis.document?.visibilityState === 'visible') check();
    };
    globalThis.document?.addEventListener('visibilitychange', onVisible);
    return () => {
      clearInterval(timer);
      globalThis.document?.removeEventListener('visibilitychange', onVisible);
    };
  }, [signedIn, client, signIn, signOut]);

  /*
    Which language this child is learning (D-189), so the device voice reads
    the course's words in the course's language and the microphone listens for
    it. English until the answer arrives, and English again at sign-out — the
    next child to tap their face may be learning something else.
  */
  useEffect(() => {
    if (learnerId === null) {
      setLearning(null);
      return;
    }
    // Not with a token the renewal is about to replace: the answer would be a
    // refusal, and English would stay all evening.
    if (!ready) return;
    let live = true;
    void (async () => {
      try {
        const { courses } = await client.course.list.query();
        const now = courses.find((course) => course.current);
        if (live && now !== undefined) {
          setLearning({ courseId: now.courseId, l2: now.l2, title: now.title });
        }
      } catch {
        // English stays: a voice in the wrong language is the worst case, and
        // it is the case every child had before there was a choice.
      }
    })();
    return () => {
      live = false;
    };
  }, [learnerId, ready, client]);

  /**
   * The chapter the path should open on next time it is drawn, or null for
   * wherever the server puts the child. Set by *Keep going* after a lesson that
   * finished a chapter, so it goes on to the next one (D-183).
   */
  const [opening, setOpening] = useState<string | null>(null);

  const leaveLesson = useCallback(() => {
    setPlaying(null);
  }, []);

  const toChapter = useCallback((chapterId: string) => {
    setOpening(chapterId);
    setPlaying(null);
  }, []);

  const openGrownUps = useCallback(() => {
    setGrownUps(true);
  }, []);

  const leaveGrownUps = useCallback(() => {
    setGrownUps(false);
  }, []);

  if (grownUps) {
    return (
      <Boundary
        what="the grown-up screen"
        fallback={<GrownUpsBroken onLeave={leaveGrownUps} />}
      >
        <GrownUps onLeave={leaveGrownUps} />
      </Boundary>
    );
  }

  if (session === null) {
    return <SignIn client={client} onSignedIn={signIn} onGrownUps={openGrownUps} />;
  }

  if (!ready) {
    return (
      <div className="frame">
        <p className="sub pad center grow" role="status">
          One moment…
        </p>
      </div>
    );
  }

  if (bonusDay !== null) {
    return (
      // Its own boundary: a bonus round that will not render must leave a child
      // a way back to the path rather than a blank screen, exactly as a lesson
      // does.
      <Boundary
        what="the bonus round"
        fallback={
          <div className="frame">
            <div className="grow pad center">
              <p className="sub" role="alert">
                The bonus round broke. Never mind!
              </p>
              <button
                className="btn"
                type="button"
                onClick={() => {
                  setBonusDay(null);
                  leaveLesson();
                }}
              >
                Back
              </button>
            </div>
          </div>
        }
      >
        <Bonus
          client={client}
          day={bonusDay}
          onLeave={() => {
            setBonusDay(null);
            leaveLesson();
          }}
        />
      </Boundary>
    );
  }

  if (playing !== null) {
    return (
      // A lesson that will not render is a child stuck on a blank screen with
      // no way back. The boundary at least leaves them the way out.
      <Boundary
        what="the lesson"
        fallback={
          <div className="frame">
            <div className="grow pad center">
              <p className="sub" role="alert">
                The lesson broke. Please try again.
              </p>
              <button className="btn" type="button" onClick={leaveLesson}>
                Back
              </button>
            </div>
          </div>
        }
      >
        {/*
          Tap-to-hint is help, so it is boxed *inside* the lesson's boundary: a
          broken hint must never take the lesson down with it.
        */}
        <HintProvider client={client}>
          <Lesson
            client={client}
            lessonId={playing === PRACTICE ? null : playing}
            replayLevel={playing === PRACTICE ? undefined : replayLevel}
            onBonus={(day) => {
              // Straight from the lesson into the round: the lesson unmounts,
              // so `playing` is cleared with it.
              setPlaying(null);
              setBonusDay(day);
            }}
            onLeave={leaveLesson}
            onNextChapter={toChapter}
          />
        </HintProvider>
      </Boundary>
    );
  }

  return (
    <div className="frame app">
      <div className="top">
        {/*
          Re-read after any change the server accepts — the shop, a chest, a
          dye, a title — and whenever a lesson ends, since `playing` is the key
          and leaving a lesson mounts a fresh one.
        */}
        <Boundary what="the wallet" fallback={null}>
          <Wallet key={playing === null ? 'path' : 'lesson'} client={client} />
        </Boundary>
        {/*
          The effects mute ([Sound](../../../docs/SOUND.md)).

          Its own switch, because the three channels mute independently: this
          silences the taps and the mallets and leaves Sneakers talking. A child
          in a quiet room needs to be able to reach it without going through a
          settings screen, so it lives in the top bar next to the wallet.
        */}
        <button
          className="btn ghost"
          type="button"
          aria-pressed={!sound}
          aria-label={sound ? 'Turn the sounds off' : 'Turn the sounds on'}
          style={{
            marginLeft: 'auto',
            width: 'auto',
            minWidth: 48,
            minHeight: 48,
            padding: '6px 12px',
            flexShrink: 0,
          }}
          onClick={() => {
            setEffects(!sound);
            setSound(!sound);
          }}
        >
          <span aria-hidden="true">{sound ? '🔊' : '🔇'}</span>
        </button>
        <button
          className="btn ghost"
          type="button"
          aria-label="Settings"
          aria-pressed={settings}
          style={{
            width: 'auto',
            minWidth: 48,
            minHeight: 48,
            padding: '6px 12px',
            flexShrink: 0,
          }}
          onClick={() => {
            setSettings(!settings);
          }}
        >
          <span aria-hidden="true">⚙️</span>
        </button>
        <button
          className="btn ghost"
          type="button"
          // `width:auto` so it does not take the whole row, and a minimum that
          // holds the tap target at 48 — the inline padding it used to carry
          // made it 29px tall, under everything the design promises. `shrink`
          // rather than a fixed width because at 200% text a rigid one is
          // pushed off the right edge instead of getting narrower.
          style={{
            width: 'auto',
            minHeight: 48,
            padding: '6px 12px',
            flexShrink: 1,
            minWidth: 0,
          }}
          onClick={signOut}
        >
          Log out
        </button>
      </div>

      {settings && (
        <Settings
          client={client}
          learnerId={session.learnerId}
          sound={sound}
          onSound={(on) => {
            setEffects(on);
            setSound(on);
          }}
          onLeave={() => {
            setSettings(false);
            setTab('learn');
          }}
          onLanguage={() => {
            // A chapter to open on belongs to the language it was in.
            setOpening(null);
          }}
        />
      )}
      {!settings && tab === 'learn' && (
        <Path
          client={client}
          opening={opening}
          onPlay={(lessonId, level) => {
            // Used once: the next lesson's ending decides again.
            setOpening(null);
            setReplayLevel(level);
            setPlaying(lessonId);
          }}
          onBonus={(day) => {
            setBonusDay(day);
          }}
          onPractise={() => {
            setReplayLevel(undefined);
            setPlaying(PRACTICE);
          }}
        />
      )}
      {/*
        Boxed on its own, because a collection that will not render must not
        cost a child the tab bar and strand them on this screen. The path is
        already boxed by the tab bar being outside this block.
      */}
      {!settings && tab === 'letters' && (
        <Boundary
          what="your letters and words"
          fallback={
            <div className="grow pad center">
              <p className="sub" role="alert">
                Your letters and words would not load. Please try again.
              </p>
            </div>
          }
        >
          <Library client={client} />
        </Boundary>
      )}
      {!settings && tab === 'quests' && (
        <Boundary
          what="your quests"
          fallback={
            <div className="grow pad center">
              <p className="sub" role="alert">
                Today&rsquo;s quests would not load. Please try again.
              </p>
            </div>
          }
        >
          <Quests client={client} />
        </Boundary>
      )}
      {!settings && tab === 'shop' && (
        <Boundary
          what="the shop"
          fallback={
            <div className="grow pad center">
              <p className="sub" role="alert">
                The shop would not load. Please try again.
              </p>
            </div>
          }
        >
          <Shop client={client} />
        </Boundary>
      )}
      {!settings && tab === 'profile' && (
        <Boundary
          what="your things"
          fallback={
            <div className="grow pad center">
              <p className="sub" role="alert">
                Your things would not load. Please try again.
              </p>
            </div>
          }
        >
          {/*
            Two things live behind the capybara tab: what a child wears, and who
            comes along with them ([Pets](../../../docs/PETS.md#the-pet-screen)).
            A switch rather than a sixth tab: the tab bar is five thumb-sized
            targets on a phone, and a sixth makes every one of them smaller for
            a screen most children open once a week.
          */}
          <div className="pad" style={{ paddingBottom: 0 }}>
            <div className="row" role="tablist" aria-label="Your things">
              {(['me', 'things', 'pets', 'badges'] as const).map((which) => (
                <button
                  key={which}
                  role="tab"
                  type="button"
                  aria-selected={mine === which}
                  className={mine === which ? 'btn' : 'btn ghost'}
                  style={{ width: 'auto', minHeight: 48 }}
                  onClick={() => {
                    setMine(which);
                  }}
                >
                  {which === 'me'
                    ? '🙂 Me'
                    : which === 'things'
                      ? '👕 Things'
                      : which === 'pets'
                        ? '🐣 Pets'
                        : '🏅 Badges'}
                </button>
              ))}
            </div>
          </div>
          {mine === 'me' && <Profile client={client} />}
          {mine === 'things' && <Wardrobe client={client} />}
          {mine === 'pets' && <Pets client={client} />}
          {mine === 'badges' && <Badges client={client} />}
        </Boundary>
      )}
      {/*
        Every tab renders something now, so there is no "coming soon" branch
        left. A placeholder that cannot be reached is worse than none: it reads
        as a promise the app is still keeping.
      */}

      <Tabs
        active={tab}
        onSelect={(next) => {
          setSettings(false);
          setTab(next);
        }}
      />
    </div>
  );
}
